import {
  Component,
  OnInit,
  EventEmitter,
  Output,
  ChangeDetectionStrategy,
  input,
  signal
} from '@angular/core';

import { NgChartsModule } from 'ng2-charts';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import {
  AlarmsDashboardService,
  AlarmDashboardData,
} from '../../services/alarms-dashboard.service';
import { MonitorsService } from '../../services/monitors.service';
import { ActivatedRoute } from '@angular/router';
import { Monitor } from '../../models/monitor.model';
import { Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    NgChartsModule,
    MatCardModule,
    MatFormFieldModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatProgressSpinnerModule,
    FormsModule,
    ReactiveFormsModule
],
  templateUrl: './dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  dataSource = input<Monitor[]>([]);
  @Output() showError = new EventEmitter<string>();

  @Output() showSuccess = new EventEmitter<string>();

  dashboardData = signal<AlarmDashboardData | null>(null);

  loading = signal(false);

  error = signal<string | null>(null);

  monitor = signal<Monitor[]>([]);

  isUnclassifiedGreater = signal(false);

  isMonitorMode = signal(true);
  monitorId = signal<string | null>(null);
  isFiltreDate = signal(false);
  filters: {
    dateRangePreset: string;
    fechaDesde: Date | null;
    fechaHasta: Date | null;
  } = {
      dateRangePreset: 'TODAY',
      fechaDesde: null,
      fechaHasta: null,
    };

  userControl = new FormControl();
  filteredUsers$: Observable<any[]> = new Observable();

  // Gráfica de distribución de estados
  public statusChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1.2,
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart',
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: 'black',
        },
      },
      title: {
        display: true,
        text: 'Distribución de Estados de Alarmas',
        color: 'black',
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleColor: 'white',
        bodyColor: 'white',
      },
    },
  };
  public statusChartData: ChartData<'pie'> = {
    labels: [],
    datasets: [
      {
        data: [],
      },
    ],
  };
  public statusChartType: ChartType = 'pie';

  // Gráfica de resumen de estados
  public summaryChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1.2,
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart',
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: 'black',
        },
      },
      title: {
        display: true,
        text: 'Resumen de Clasificación',
        color: 'black',
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleColor: 'white',
        bodyColor: 'white',
      },
    },
  };
  public summaryChartData: ChartData<'doughnut'> = {
    labels: [],
    datasets: [
      {
        data: [],
      },
    ],
  };
  public summaryChartType: ChartType = 'doughnut';

  // Gráfica de distribución de eventos
  public eventChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1.6,
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart',
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Distribución de Eventos',
        color: 'black',
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleColor: 'white',
        bodyColor: 'white',
      },
    },
  };
  public eventChartData: ChartData<'bar'> = {
    labels: [],
    datasets: [
      {
        data: [],
      },
    ],
  };
  public eventChartType: ChartType = 'bar';

  // Gráfica de serie temporal
  public timeSeriesChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1.6,
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart',
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Serie Temporal de Alarmas',
        color: 'black',
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleColor: 'white',
        bodyColor: 'white',
      },
    },
  };
  public timeSeriesChartData: ChartData<'line'> = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Alarmas',
      },
    ],
  };
  public timeSeriesChartType: ChartType = 'line';

  constructor(
    private monitorsService: MonitorsService,
    private dashboardService: AlarmsDashboardService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.initializeFilters();
    // Leer monitorId desde paramMap y recargar solo el dashboard cuando cambie
    this.route.paramMap.subscribe((params) => {
      const id = params.get('monitorId');
      this.monitorId.set(id);
      this.loadDashboardData();
    });
  }

  private initializeFilters(): void {
    this.filteredUsers$ = this.userControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filterUsers(value))
    );
  }

  private _filterUsers(value: any): any[] {
    return [];
  }

  onDateRangePresetChange(value: string): void {
    this.isFiltreDate.set(!value);
  }

  onMonitorChange(selectedId: string): void {
    this.monitorId.set(selectedId || null);
    this.loadDashboardData();
  }

  onUserSelected(user: any): void {
    // Manejar selección de usuario
  }

  applyFilters(): void {
  
    // Validación 1: Verificar si al menos un filtro está seleccionado
    const hasDateRangePreset = this.filters.dateRangePreset && this.filters.dateRangePreset !== '';
    const hasDateRange = this.filters.fechaDesde && this.filters.fechaHasta;
    const hasMonitor = this.monitorId() && this.monitorId() !== '';

    if (!hasDateRangePreset && !hasDateRange && !hasMonitor) {
      const msg = 'Error: Debe seleccionar al menos un filtro (Monitor o Rango de fechas).';

      this.showError.emit(msg);
      return;
    }

    // Validación 2: Si Rango rápido es "Ninguno" o vacío, validar fechas personalizadas
    if (!hasDateRangePreset) {
      if (!this.filters.fechaDesde) {
        const msg = 'Error: Debe seleccionar la fecha desde cuando el rango rápido es "Ninguno".';
        this.showError.emit(msg);
        return;
      }
      if (!this.filters.fechaHasta) {
        const msg = 'Error: Debe seleccionar la fecha hasta cuando el rango rápido es "Ninguno".';
        this.showError.emit(msg);
        return;
      }
    }

    // Validación 3: Validar que fechaDesde no sea mayor que fechaHasta
    if (hasDateRange && this.filters.fechaDesde && this.filters.fechaHasta) {
      const fechaDesde = new Date(this.filters.fechaDesde);
      const fechaHasta = new Date(this.filters.fechaHasta);

      if (fechaDesde > fechaHasta) {
        const msg = 'Error: La fecha desde no puede ser mayor a la fecha hasta.';
        console.error(msg, { fechaDesde, fechaHasta });
        alert(msg);
        return;
      }

      
    }

    // Validación 4: Verificar rango rápido si está seleccionado
    const validPresets = ['TODAY', 'LAST_7_DAYS', 'LAST_MONTH', 'LAST_3_MONTHS'];
    if (hasDateRangePreset && !validPresets.includes(this.filters.dateRangePreset)) {
      const msg = `Error: Rango rápido inválido: ${this.filters.dateRangePreset}`;
      console.error(msg);
      alert(msg);
      return;
    }

    

    // Build query parameters: if dateRangePreset is 'ninguno' or empty, use explicit dates; otherwise use granularity
    const opts: any = {};

    const fmt = (d: Date, endOfDay = false) => {
      const pad = (n: number) => String(n).padStart(2, '0');
      const year = d.getFullYear();
      const month = pad(d.getMonth() + 1);
      const day = pad(d.getDate());
      const hours = pad(endOfDay ? 23 : d.getHours());
      const minutes = pad(endOfDay ? 59 : d.getMinutes());
      const seconds = pad(endOfDay ? 59 : d.getSeconds());
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    };

    if (this.filters.dateRangePreset && this.filters.dateRangePreset !== '') {
      opts.granularity = this.filters.dateRangePreset;
    } else if (this.filters.fechaDesde && this.filters.fechaHasta) {
      opts.startDate = fmt(new Date(this.filters.fechaDesde));
      opts.endDate = fmt(new Date(this.filters.fechaHasta), true);
      
    } else {
      
    }

    // Si no hay granularity y no hay startDate/endDate, NO asignar valores por defecto
    // Solo enviar lo que el usuario seleccionó explícitamente
    if (!opts.granularity && !opts.startDate && !opts.endDate) {
      
    }

    const currentMonitorId = this.monitorId();
    if (currentMonitorId && currentMonitorId !== '') {
      opts.monitorId = currentMonitorId;
    } else {
      
    }
    

    this.loading.set(true);
    this.error.set(null);

    this.monitorsService.getMetrics(opts).subscribe({
      next: (data) => {
        this.dashboardData.set(data);
        this.updateCharts();
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Error al aplicar filtros: ' + err.message);
        this.loading.set(false);
      },
    });
  }

  resetFilters(): void {
    // Limpiar filtros
    this.filters = {
      dateRangePreset: '',
      fechaDesde: null,
      fechaHasta: null,
    };
    this.userControl.reset();
  }

  ngOnChanges(): void {
    if (this.dataSource().length > 0) {
      this.monitor.set([...this.dataSource()]);
    }
  }

  private loadDashboardData(): void {
    if (this.dataSource().length > 0) {
      this.monitor.set(this.dataSource() as Monitor[]);
    }

    this.loading.set(true);
    this.error.set(null);

    this.monitorsService.getMetrics().subscribe({
      next: (data: any) => {
        this.dashboardData.set(data);
        this.updateCharts();
        this.loading.set(false);
      },
      error: (err: any) => {
        this.error.set('Error al cargar las metrics: ' + (err?.message || String(err)));
        this.loading.set(false);
      },
    });
  }

  loadMonitors(): void {
    this.error.set(null);

    this.monitorsService.getMonitors().subscribe({
      next: (data: any) => {
        this.monitor.set(data as Monitor[]);
      },
      error: (err: any) => {
        this.error.set('Error al cargar los monitores: ' + (err?.message || String(err)));
      },
    });
  }

  onShowError(message: string): void {
    this.showError.emit(message);
  }

  onShowSuccess(message: string): void {
    this.showSuccess.emit(message);
  }


  private updateCharts(): void {
    const data = this.dashboardData();
    if (!data) return;

    this.statusChartData.labels = data.statusTypeDistribution.map(
      (item) => `${item.status} (${item.percentage}%)`
    );
    this.statusChartData.datasets[0].data = data.statusTypeDistribution.map(
      (item) => item.count
    );
    this.statusChartData.datasets[0].backgroundColor = [
      '#dc3545',
      '#ffc107',
      '#28a745',
      '#6c757d',
      '#17a2b8',
    ];
    this.statusChartData.datasets[0].borderColor = [
      '#b02a37',
      '#e0a800',
      '#1e7e34',
      '#545b62',
      '#0c5460',
    ];
    this.statusChartData.datasets[0].borderWidth = 3;
    this.statusChartData.datasets[0].hoverBackgroundColor = [
      'rgba(220, 53, 69, 0.8)',
      'rgba(255, 193, 7, 0.8)',
      'rgba(40, 167, 69, 0.8)',
      'rgba(108, 117, 125, 0.8)',
      'rgba(23, 162, 184, 0.8)',
    ];

    this.summaryChartData.labels = ['Sin Clasificar', 'Clasificadas'];
    this.summaryChartData.datasets[0].data = [
      data.statusSummary.unclassified,
      data.statusSummary.classified,
    ];
    this.summaryChartData.datasets[0].backgroundColor = ['#fd7e14', '#007bff'];
    this.summaryChartData.datasets[0].borderColor = ['#e8680f', '#0056b3'];
    this.summaryChartData.datasets[0].borderWidth = 3;
    this.summaryChartData.datasets[0].hoverBackgroundColor = [
      'rgba(253, 126, 20, 0.8)',
      'rgba(0, 123, 255, 0.8)',
    ];

    this.eventChartData.labels = data.eventDistribution.map(
      (e) => e.eventName
    );
    this.eventChartData.datasets[0].data =
      data.eventDistribution.map((e) => e.count);
    this.eventChartData.datasets[0].backgroundColor = [
      '#007bff',
      '#28a745',
      '#dc3545',
      '#ffc107',
    ];
    this.eventChartData.datasets[0].borderColor = [
      '#0056b3',
      '#1e7e34',
      '#b02a37',
      '#e0a800',
    ];
    this.eventChartData.datasets[0].borderWidth = 2;
    this.eventChartData.datasets[0].hoverBackgroundColor = [
      'rgba(0, 123, 255, 0.8)',
      'rgba(40, 167, 69, 0.8)',
      'rgba(220, 53, 69, 0.8)',
      'rgba(255, 193, 7, 0.8)',
    ];

    this.timeSeriesChartData.labels = data.alarmTimeSeries.map(
      (t) => {
        const d = new Date(t.timestamp);
        return d.toLocaleDateString('es-ES');
      }
    );
    this.timeSeriesChartData.datasets[0].data =
      data.alarmTimeSeries.map((t) => t.count);
    this.timeSeriesChartData.datasets[0].backgroundColor =
      'rgba(0, 123, 255, 0.2)';
    this.timeSeriesChartData.datasets[0].borderColor = '#007bff';
    this.timeSeriesChartData.datasets[0].borderWidth = 3;
    this.timeSeriesChartData.datasets[0].fill = true;
    this.timeSeriesChartData.datasets[0].hoverBackgroundColor =
      'rgba(0, 123, 255, 0.4)';
    this.timeSeriesChartData.datasets[0].hoverBorderColor = '#0056b3';

    this.isUnclassifiedGreater.set(
      (data.statusSummary.unclassified || 0) >
      (data.statusSummary.classified || 0)
    );
  }
}
